// ============================================================================
// TAX META SERVICE (ADR-0019) — reference reads + the calc seam.
// ============================================================================

import { get, post } from '../client/axiosClient';
import { taxMetaEndpoints } from '../endpoints/tax.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  TaxJurisdictionDto, TaxComponentDto, SupplyTypeDto, TaxRateLookupDto, TaxFieldContextDto,
  TaxCalculationRequest, TaxCalculationResult, TaxPackDto, RegistrationNumberParseDto,
  InputTaxRulesDto,
} from '../types';

class TaxMetaService {
  async getJurisdictions(): Promise<TaxJurisdictionDto[]> {
    const r = await get<ApiResponse<TaxJurisdictionDto[]>>(taxMetaEndpoints.jurisdictions());
    return r.data.data ?? [];
  }
  async getEntityJurisdiction(): Promise<TaxJurisdictionDto | null> {
    const r = await get<ApiResponse<TaxJurisdictionDto | null>>(taxMetaEndpoints.jurisdiction());
    return r.data.data ?? null;
  }
  async getFieldContext(target: string): Promise<TaxFieldContextDto> {
    const url = buildEndpointWithQuery(taxMetaEndpoints.fieldContext(), { target });
    const r = await get<ApiResponse<TaxFieldContextDto>>(url);
    return r.data.data;
  }
  async getSupplyTypes(): Promise<SupplyTypeDto[]> {
    const r = await get<ApiResponse<SupplyTypeDto[]>>(taxMetaEndpoints.supplyTypes());
    return r.data.data ?? [];
  }
  async getRates(): Promise<TaxRateLookupDto[]> {
    const r = await get<ApiResponse<TaxRateLookupDto[]>>(taxMetaEndpoints.rates());
    return r.data.data ?? [];
  }
  async getComponents(): Promise<TaxComponentDto[]> {
    const r = await get<ApiResponse<TaxComponentDto[]>>(taxMetaEndpoints.components());
    return r.data.data ?? [];
  }
  /** The jurisdiction's input-tax rules (component sets + ITC preconditions, ADR-0049 §4a). */
  async getInputTaxRules(): Promise<InputTaxRulesDto | null> {
    const r = await get<ApiResponse<InputTaxRulesDto>>(taxMetaEndpoints.inputTaxRules());
    return r.data.data ?? null;
  }
  /**
   * The validated engine pack for a jurisdiction (ADR-0047 / TaxCore ADR-0022): components,
   * published slab bases, places of supply, supply types. Omit the code to resolve the
   * entity's jurisdiction from X-Entity-ID. Throws with MIGRATION_PENDING / TAX_PACK_INVALID
   * when the tenant schema or the tax data cannot be computed over.
   */
  async getPack(jurisdictionCode?: string): Promise<TaxPackDto> {
    const url = jurisdictionCode
      ? buildEndpointWithQuery(taxMetaEndpoints.pack(), { jurisdictionCode })
      : taxMetaEndpoints.pack();
    const r = await get<ApiResponse<TaxPackDto>>(url);
    return r.data.data;
  }
  /**
   * What the entity's jurisdiction makes of a registration number as it is typed: well formed or
   * not and, where the number encodes the place of registration (India GST), the state it resolves
   * to. Feeds the derived state field of the registration and client forms; the server derives
   * the same again on save, so this is a preview, never the rule.
   */
  async parseRegistrationNumber(number: string): Promise<RegistrationNumberParseDto> {
    const url = buildEndpointWithQuery(taxMetaEndpoints.parseRegistrationNumber(), { number });
    const r = await get<ApiResponse<RegistrationNumberParseDto>>(url);
    return r.data.data;
  }
  async calculate(request: TaxCalculationRequest): Promise<TaxCalculationResult> {
    const r = await post<ApiResponse<TaxCalculationResult>>(taxMetaEndpoints.calculate(), request);
    return r.data.data;
  }
}

export const taxMetaService = new TaxMetaService();
export default taxMetaService;
